/**
 * TDM-D: useFavoriteTools — tracks user's pinned drawing tools, persisted in localStorage
 * P3: canonical key is `vnsc_favTools` (was `rsc_favorite_tools`); the legacy
 * key is migrated one-time on load so existing users keep their pins.
 */
import { useCallback, useSyncExternalStore } from "react";
const STORAGE_KEY = "vnsc_favTools";
const LEGACY_STORAGE_KEY = "rsc_favorite_tools";
const STORAGE_VISIBILITY_KEY = "vnsc_favToolsBarVisible";
function parseTools(raw) {
    try {
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : null;
    }
    catch {
        return null;
    }
}
function loadFavorites() {
    try {
        const current = parseTools(localStorage.getItem(STORAGE_KEY));
        if (current)
            return current;
        const legacy = parseTools(localStorage.getItem(LEGACY_STORAGE_KEY));
        if (legacy) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(legacy));
            return legacy;
        }
        return [];
    }
    catch {
        return [];
    }
}
function saveFavorites(tools) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(tools));
    }
    catch {
        // ignore storage errors
    }
}
function loadVisibility() {
    try {
        const raw = localStorage.getItem(STORAGE_VISIBILITY_KEY);
        if (raw === null)
            return true; // Default visible when not set
        return raw === "true";
    }
    catch {
        return true;
    }
}
function saveVisibility(visible) {
    try {
        localStorage.setItem(STORAGE_VISIBILITY_KEY, String(visible));
    }
    catch {
        // ignore storage errors
    }
}
// Module-level Single Source of Truth store
let cachedFavorites = loadFavorites();
let cachedVisibility = loadVisibility();
const listeners = new Set();
function notify() {
    listeners.forEach((listener) => {
        try {
            listener();
        }
        catch {
            // ignore listener errors
        }
    });
}
function subscribe(listener) {
    const currentFavs = loadFavorites();
    if (JSON.stringify(currentFavs) !== JSON.stringify(cachedFavorites)) {
        cachedFavorites = currentFavs;
    }
    const currentVis = loadVisibility();
    if (currentVis !== cachedVisibility) {
        cachedVisibility = currentVis;
    }
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}
export function reloadFavoritesStore() {
    cachedFavorites = loadFavorites();
    cachedVisibility = loadVisibility();
    notify();
}
if (typeof window !== "undefined") {
    window.addEventListener("storage", (e) => {
        if (e.key === STORAGE_KEY || e.key === LEGACY_STORAGE_KEY) {
            cachedFavorites = loadFavorites();
            notify();
        }
        else if (e.key === STORAGE_VISIBILITY_KEY) {
            cachedVisibility = loadVisibility();
            notify();
        }
    });
}
export function dispatchToggleFavoritesBar() {
    cachedVisibility = !cachedVisibility;
    saveVisibility(cachedVisibility);
    notify();
}
export function dispatchSetFavoritesBarVisible(visible) {
    if (cachedVisibility !== visible) {
        cachedVisibility = visible;
        saveVisibility(cachedVisibility);
        notify();
    }
}
function getFavoritesSnapshot() {
    const current = loadFavorites();
    if (JSON.stringify(current) !== JSON.stringify(cachedFavorites)) {
        cachedFavorites = current;
    }
    return cachedFavorites;
}
function getVisibilitySnapshot() {
    const current = loadVisibility();
    if (current !== cachedVisibility) {
        cachedVisibility = current;
    }
    return cachedVisibility;
}
export function useFavoriteTools() {
    const favoriteTools = useSyncExternalStore(subscribe, getFavoritesSnapshot, () => []);
    const isFavoritesBarVisible = useSyncExternalStore(subscribe, getVisibilitySnapshot, () => true);
    const setFavoritesBarVisible = useCallback((visible) => {
        dispatchSetFavoritesBarVisible(visible);
    }, []);
    const toggleFavoritesBarVisibility = useCallback(() => {
        dispatchToggleFavoritesBar();
    }, []);
    const toggleFavorite = useCallback((toolId) => {
        const prevLen = cachedFavorites.length;
        const next = cachedFavorites.includes(toolId)
            ? cachedFavorites.filter((id) => id !== toolId)
            : [...cachedFavorites, toolId];
        cachedFavorites = next;
        saveFavorites(next);
        // Auto-reveal the bar when user pins their first tool
        if (prevLen === 0 && next.length > 0) {
            cachedVisibility = true;
            saveVisibility(true);
        }
        notify();
    }, []);
    const isFavorite = useCallback((toolId) => favoriteTools.includes(toolId), [favoriteTools]);
    return {
        favoriteTools,
        toggleFavorite,
        isFavorite,
        isFavoritesBarVisible,
        toggleFavoritesBarVisibility,
        setFavoritesBarVisible,
    };
}
